import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import apiCall from "../../API/API";
import "../PlanSettings/PlanSettings.css";
import "../ActivationRequests/ActivationRequests.css";
import "./SubAdmins.css";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const EMPTY = { uid: null, name: "", username: "", email: "", password: "", modules: [], monthly_limit: "", status: 1 };

const UsageBar = ({ used, limit }) => {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : used > 0 ? 100 : 0;
  const tone = pct >= 90 ? "danger" : pct >= 70 ? "warn" : "ok";
  return (
    <div className="sa-usage">
      <div className="sa-usage-head">
        <span>
          <strong>{inr(used)}</strong> of {inr(limit)}
        </span>
        <span className="ps-muted">{inr(Math.max(0, limit - used))} left</span>
      </div>
      <div className="sa-bar">
        <div className={`sa-bar-fill sa-bar-fill--${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const SubAdminModal = ({ show, initial, modules, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isNew = !initial?.uid;

  useEffect(() => {
    if (show) setForm(initial ? { ...EMPTY, ...initial, password: "" } : EMPTY);
  }, [show, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleModule = (key) =>
    setForm((f) => ({
      ...f,
      modules: f.modules.includes(key) ? f.modules.filter((m) => m !== key) : [...f.modules, key],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      name: form.name,
      email: form.email,
      modules: form.modules,
      monthly_limit: Number(form.monthly_limit),
      status: Number(form.status),
    };
    if (isNew) body.username = form.username;
    else body.uid = form.uid;
    if (form.password) body.password = form.password;
    const { data, error } = await apiCall("POST", "/sub-admins/save", body);
    setSaving(false);
    if (error) return toast.error(error.message || "Could not save.");
    toast.success(data?.message || "Saved.");
    onSaved();
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" className="plan-settings-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{isNew ? "New Sub Admin" : `Edit ${initial?.username}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ps-form-grid">
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control value={form.name} onChange={set("name")} required maxLength={60} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Username (for login)</Form.Label>
              <Form.Control
                value={form.username}
                onChange={set("username")}
                required={isNew}
                disabled={!isNew}
                placeholder="e.g. ravi_ops"
                autoComplete="off"
              />
              <Form.Text>{isNew ? "4–20 letters, digits or _; cannot be changed later." : "Usernames cannot be changed."}</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={form.email} onChange={set("email")} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>{isNew ? "Password" : "New password (optional)"}</Form.Label>
              <Form.Control
                type="text"
                value={form.password}
                onChange={set("password")}
                required={isNew}
                placeholder={isNew ? "8+ chars, upper, lower, number" : "Leave blank to keep the current one"}
                autoComplete="new-password"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Monthly approval limit (₹)</Form.Label>
              <Form.Control
                type="number"
                min={0}
                step="any"
                value={form.monthly_limit}
                onChange={set("monthly_limit")}
                required
                placeholder="e.g. 1000000"
              />
              <Form.Text>
                {Number(form.monthly_limit) > 0
                  ? `${inr(form.monthly_limit)} of package activations per month. Resets on the 1st.`
                  : "0 = cannot approve or activate packages."}
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select value={form.status} onChange={set("status")}>
                <option value={1}>Active — can log in</option>
                <option value={0}>Disabled — cannot log in</option>
              </Form.Select>
            </Form.Group>
          </div>

          <div className="sa-modules">
            <div className="sa-modules-head">
              <Form.Label className="m-0">Pages this sub-admin can open</Form.Label>
              <div className="sa-modules-actions">
                <Button size="sm" variant="link" onClick={() => setForm((f) => ({ ...f, modules: modules.map((m) => m.key) }))}>
                  Select all
                </Button>
                <Button size="sm" variant="link" onClick={() => setForm((f) => ({ ...f, modules: [] }))}>
                  Clear
                </Button>
              </div>
            </div>
            <div className="sa-module-grid">
              {modules.map((m) => (
                <label key={m.key} className={`sa-module${form.modules.includes(m.key) ? " sa-module--on" : ""}`}>
                  <input type="checkbox" checked={form.modules.includes(m.key)} onChange={() => toggleModule(m.key)} />
                  <span>
                    <strong>{m.label}</strong>
                    {m.note && <small>{m.note}</small>}
                  </span>
                </label>
              ))}
            </div>
            <div className="ps-note ps-note-info">
              Plan Settings, Settings, Sub Admins and the Audit Log stay with the main admin only.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" className="ar-primary-btn" disabled={saving || form.modules.length === 0}>
            {saving ? <Spinner size="sm" /> : isNew ? "Create Sub Admin" : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const SubAdmins = () => {
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiCall("GET", "/sub-admins");
    setLoading(false);
    if (error) return toast.error(error.message || "Could not load sub-admins.");
    setRows(data?.data || []);
    setModules(data?.modules || []);
    setMonth(data?.month || "");
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const moduleLabel = (key) => modules.find((m) => m.key === key)?.label || key;
  const active = rows.filter((r) => r.status === 1);
  const totalLimit = active.reduce((a, r) => a + r.monthly_limit, 0);
  const totalUsed = active.reduce((a, r) => a + r.used_this_month, 0);

  return (
    <div className="plan-settings activation-requests-page sub-admins-page">
      <div className="ps-header">
        <div>
          <h2>Sub Admins</h2>
          <p>Staff logins with chosen pages and a monthly limit for package approvals. Every action is in the Audit Log.</p>
        </div>
        <Button className="ar-primary-btn" onClick={() => { setEditing(null); setShowModal(true); }}>
          + New Sub Admin
        </Button>
      </div>

      <div className="ps-stats ar-stats">
        <div className="ps-stat ar-stat">
          <span>Sub admins</span>
          <strong>{active.length}</strong>
          <small>{rows.length - active.length} disabled</small>
        </div>
        <div className="ps-stat ar-stat">
          <span>Approved in {month || "this month"}</span>
          <strong>{inr(totalUsed)}</strong>
          <small>by sub-admins</small>
        </div>
        <div className="ps-stat ar-stat">
          <span>Limits left</span>
          <strong>{inr(Math.max(0, totalLimit - totalUsed))}</strong>
          <small>of {inr(totalLimit)} in total</small>
        </div>
      </div>

      <div className="ps-panel" style={{ borderTop: "1px solid var(--ps-border)", borderRadius: "var(--ps-radius)" }}>
        <div className="ps-table-wrap">
          <Table className="ps-table">
            <thead>
              <tr>
                <th>Sub admin</th>
                <th>Pages</th>
                <th>{month || "This month"}</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="ps-empty"><Spinner size="sm" /> Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="ps-empty">No sub-admins yet. Create one to share the approval work.</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.uid}>
                    <td>
                      <div className="ps-strong">{r.name}</div>
                      <div className="ps-muted ps-small">@{r.username} · {r.email}</div>
                      <div className="ps-muted ps-small">Since {moment(r.created_at).format("DD MMM YYYY")}</div>
                    </td>
                    <td>
                      <div className="sa-chips">
                        {r.modules.map((m) => (
                          <span className="sa-chip" key={m}>{moduleLabel(m)}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{ minWidth: 220 }}>
                      <UsageBar used={r.used_this_month} limit={r.monthly_limit} />
                    </td>
                    <td>
                      {r.status === 1 ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Disabled</Badge>}
                    </td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-primary" onClick={() => { setEditing(r); setShowModal(true); }}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      </div>

      <SubAdminModal
        show={showModal}
        initial={editing}
        modules={modules}
        onClose={() => setShowModal(false)}
        onSaved={() => { setShowModal(false); load(); }}
      />
    </div>
  );
};

export default SubAdmins;
